import type { MouseEvent } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  LARGE_PREVIEW_TOOLBAR_HEIGHT_PX,
  largePreviewButtonFocusSx,
  largePreviewToolbarCounterSx,
  largePreviewToolbarNavBtnSx,
  largePreviewToolbarShellSx,
} from './notePreviewUi';
import type { LargePreviewKeyboardContext } from './useLargePreviewMediaKeyboard';

type LargePreviewMediaActionsProps = {
  previewMode: 'media' | 'grid';
  candidateCount: number;
  currentIndex: number;
  onPrev: () => void;
  onNext: () => void;
  canYoutubeSpace: boolean;
  isYoutubePlaying: boolean;
  onPlayClick: () => void;
  keyboardContext?: LargePreviewKeyboardContext;
  themed?: boolean;
};

/** Same padding as the 9/62 counter chip — keeps buttons shorter inside the bar. */
const toolbarChipSx = {
  px: 1.25,
  py: 0.35,
  borderRadius: 1.25,
  boxSizing: 'border-box',
} as const;

const toolbarIconBtnBase = {
  ...toolbarChipSx,
  width: '100%',
  minWidth: 72,
  height: 'auto',
  minHeight: 'unset',
  maxHeight: 'none',
  padding: '0.35rem 1.25rem',
} as const;

const toolbarPlayBtnSx = {
  ...largePreviewButtonFocusSx,
  ...toolbarIconBtnBase,
  border: '1px solid',
  borderColor: 'primary.main',
  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.18)',
  transition: 'background .18s, box-shadow .18s, transform .12s, border-color .25s ease',
  '&:hover:not(.Mui-disabled)': {
    boxShadow: '0 10px 24px rgba(25, 118, 210, 0.28)',
    transform: 'scale(1.03)',
  },
} as const;

function blurAfterClick(handler: () => void) {
  return (e: MouseEvent<HTMLButtonElement>) => {
    handler();
    e.currentTarget.blur();
  };
}

function NavChevrons({
  direction,
}: {
  direction: 'left' | 'right';
}) {
  const Single = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon;
  const Double = direction === 'left' ? KeyboardDoubleArrowLeftIcon : KeyboardDoubleArrowRightIcon;
  const iconSx = { fontSize: 20 };

  return (
    <>
      <Box className="nav-icon-single" sx={{ alignItems: 'center', justifyContent: 'center' }}>
        <Single sx={iconSx} />
      </Box>
      <Box className="nav-icon-double" sx={{ alignItems: 'center', justifyContent: 'center' }}>
        <Double sx={iconSx} />
      </Box>
    </>
  );
}

export function LargePreviewMediaActions({
  previewMode,
  candidateCount,
  currentIndex,
  onPrev,
  onNext,
  canYoutubeSpace,
  isYoutubePlaying,
  onPlayClick,
  keyboardContext = 'gallery',
  themed = false,
}: LargePreviewMediaActionsProps) {
  if (candidateCount < 1) {
    return null;
  }

  const toolbarNavBtnSx = largePreviewToolbarNavBtnSx(keyboardContext, themed);
  const toolbarShellSx = largePreviewToolbarShellSx(keyboardContext, themed);
  const toolbarCounterSx = largePreviewToolbarCounterSx(keyboardContext, themed);

  const showNav = candidateCount > 1;
  const slotCount = (showNav ? 3 : 1) + 1;
  const prevTitle = previewMode === 'grid' ? 'Previous tile (←)' : 'Previous (←)';
  const nextTitle = previewMode === 'grid' ? 'Next tile (→)' : 'Next (→)';
  const playDisabled = !canYoutubeSpace;
  const playLabel = isYoutubePlaying ? 'Pause (Space)' : 'Play (Space)';
  const PlayIcon = isYoutubePlaying ? PauseIcon : PlayArrowIcon;

  const slotSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 0,
    px: 0.5,
  } as const;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        mr: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${slotCount}, minmax(0, 1fr))`,
        alignItems: 'center',
        height: LARGE_PREVIEW_TOOLBAR_HEIGHT_PX,
        maxHeight: LARGE_PREVIEW_TOOLBAR_HEIGHT_PX,
        py: 0,
        px: 0.75,
        gap: 0.75,
        borderRadius: 1.25,
        border: '1px solid',
        ...toolbarShellSx,
      }}
    >
      {showNav && (
        <Box sx={slotSx}>
          <Tooltip title={prevTitle}>
            <span style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <IconButton
                size="small"
                onClick={blurAfterClick(onPrev)}
                disabled={currentIndex <= 0}
                sx={toolbarNavBtnSx}
                aria-label={previewMode === 'grid' ? 'Previous tile' : 'Previous video'}
              >
                <NavChevrons direction="left" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      <Box sx={slotSx}>
        <Tooltip title={playLabel}>
          <span style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <IconButton
              size="small"
              color="primary"
              onClick={blurAfterClick(onPlayClick)}
              disabled={playDisabled}
              sx={toolbarPlayBtnSx}
              aria-label={playLabel}
            >
              <PlayIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {showNav && (
        <Box sx={slotSx}>
          <Tooltip title={nextTitle}>
            <span style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <IconButton
                size="small"
                onClick={blurAfterClick(onNext)}
                disabled={currentIndex >= candidateCount - 1}
                sx={toolbarNavBtnSx}
                aria-label={previewMode === 'grid' ? 'Next tile' : 'Next video'}
              >
                <NavChevrons direction="right" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      <Box sx={slotSx}>
        <Typography
          component="span"
          sx={toolbarCounterSx}
        >
          {currentIndex + 1}/{candidateCount}
        </Typography>
      </Box>
    </Box>
  );
}
